import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import { PhCheckCircle, PhCircle, PhCircleDashed } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';

interface ChecklistRowSignature {
  Args: {
    title: string;
    /** Sits under the title. Rendered in both states, so keep it durable. */
    hint?: string;
    isDone: boolean;
    /**
     * The row renders but nothing enforces it, so it is excluded from the
     * card's count. Takes a muted marker and an Optional chip instead of the
     * satisfied check — wearing the same green tick as a real requirement made
     * a two-requirement card look like it was miscounting three.
     */
    isOptional?: boolean;
  };
  Blocks: {
    /** The control for an outstanding row: a select, an uploader, a checkbox. */
    default: [];
    /**
     * Replaces the control once satisfied — usually the saved value plus Edit.
     * Omit it and the control keeps rendering, which is what a row wants when
     * its control stays useful after the requirement is met (an uploader still
     * has to list and accept more files).
     */
    done?: [];
  };
  Element: HTMLDivElement;
}

/**
 * One requirement inside the evidence card, drawn as a state marker plus its
 * own control.
 *
 * Every requirement uses this row so the grade, the uploads and the terms read
 * as one list rather than three unrelated widgets — which is also what lets the
 * collapsed card summarise them with a single count.
 *
 * Rows carry no number. The grade and the documents are independent, so
 * numbering would assert an order that does not exist; only the submit action
 * is genuinely last, and it says so in words instead.
 */
const ChecklistRow: TOC<ChecklistRowSignature> = <template>
  <div
    class="tio-col gap-2 rounded-lg tio-bg-sunken p-3"
    data-test-checklist-row
    data-test-checklist-row-done={{if @isDone "true" "false"}}
    ...attributes
  >
    <div class="tio-row items-start gap-2">
      {{#if @isOptional}}
        <PhCircleDashed @size={{20}} class="tio-fg-muted shrink-0" />
      {{else if @isDone}}
        <PhCheckCircle @size={{20}} @weight="fill" class="tio-fg-success shrink-0" />
      {{else}}
        <PhCircle @size={{20}} class="tio-fg-secondary shrink-0" />
      {{/if}}
      <div class="tio-col min-w-0 gap-1">
        <div class="tio-row min-w-0 flex-wrap items-center gap-2">
          <span class="tio-text-sm-semibold tio-fg-primary">{{@title}}</span>
          {{#if @isOptional}}
            <Chip @intent="default" @size="sm" data-test-checklist-row-optional>
              {{t "tuition_assistance.applications.manage.tasks.checklist_optional"}}
            </Chip>
          {{/if}}
        </div>
        {{#if @hint}}
          <span class="tio-text-sm-normal tio-fg-secondary">{{@hint}}</span>
        {{/if}}
      </div>
    </div>
    {{! Indented to the marker's column so a row's control reads as belonging to
      its heading — but only once there is width to spare. Below 40rem the
      indent squeezes the uploader's own row layout until its button overlaps
      the wrapped label. }}
    <div class="sm:pl-6">
      {{#if (and @isDone (has-block "done"))}}
        {{yield to="done"}}
      {{else}}
        {{yield}}
      {{/if}}
    </div>
  </div>
</template>;

export default ChecklistRow;
